import React, { useEffect, useState, useContext } from "react";
import { RouteComponentProps, Prompt } from "react-router-dom";
import useSnackbar from "../../hooks/snackbar/useSnackbar";
import UserContext from "../../UserContext";
import StepperComponent from "../shared/common/StepperComponent";
import TrSnackbar from "../shared/controls/TrSnackbar";
import PaymentRequestSelectStep from "./select/PaymentRequestSelectStep";
import PaymentRequestSchoolCarrierDataStep from "./school-carrier-data/PaymentRequestSchoolCarrierDataStep";
import PaymentRequestAuthorizedCarrierDataStep from "./authorized-carrier-data/PaymentRequestAuthorizedCarrierDataStep";
import PaymentRequestStudentDataStep from "./student-data/PaymentRequestStudentDataStep";
import PaymentRequestDataConfirmationStep from "./data-confirmation/PaymentRequestDataConfirmationStep";
import PaymentRequest from "../../models/payment-request/PaymentRequest";
import PaymentRequestStudent from "../../models/payment-request/PaymentRequestStudent";
import PaymentRequestStudentData from "../../models/payment-request/PaymentRequestStudentData";
import PaymentRequestSchoolCarrier from "../../models/payment-request/PaymentRequestSchoolCarrier";
import PaymentRequestSchoolCarrierData from "../../models/payment-request/PaymentRequestSchoolCarrierData";
import PaymentRequestAuthorizedCarrier from "../../models/payment-request/PaymentRequestAuthorizedCarrier";
import PaymentRequestAuthorizedCarrierData from "../../models/payment-request/PaymentRequestAuthorizedCarrierData";
import SchoolType from "../../models/school-type/SchoolType";
import SchoolYear from "../../models/school-year/SchoolYear";
import PaymentRequestApiClient from "../../api/PaymentRequestApiClient";
import { paymentRequestsUrl } from "../../urls";
import Currency from "../../models/shared/Currency";
import AnnualAdministration from "../../models/admin/annual-administration/AnnualAdministration";
import AnnualAdministrationPage from "../admin/annual-administration/AnnualAdministrationPage";

interface MatchParams {
  id: string;
}

interface Props extends RouteComponentProps<MatchParams> { }

export default function PaymentRequestPage({ match, history }: Props) {
  const { id } = match.params;
  const { user } = useContext(UserContext);

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest>({
    schoolTypeId: user.institutionTypes[0],
    schoolYearId: 0,
    currency: Currency.Euro,//default value, expecting to be something else
    monthDate: null,
    students: [],
    studentsData: [],
    schoolCarriers: [],
    schoolCarriersData: [],
    authorizedCarriers: [],
    authorizedCarriersData: [],
    annualAdministration: {} as AnnualAdministration
  });
  const [schoolTypes, setSchoolTypes] = useState<SchoolType[]>([]);
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [showWarningMessage, setShowWarningMessage] = useState<boolean>(true);
  const [latestStudentsDataFromStep, setLatestStudentsDataFromStep] = useState<PaymentRequestStudentData[]>([]);


  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
    variant: snackbarVariant,
  } = useSnackbar();

  useEffect(() => {
    console.log("PAYMENT_CHANGE");
    console.log(paymentRequest);
  }, [paymentRequest]);

  useEffect(() => {
    if (!id) return;

    async function getPaymentRequestForEdit(paymentRequestId: string) {
      const apiClient = new PaymentRequestApiClient();
      const editPaymentRequest = await apiClient.getPaymentRequestForEdit(+paymentRequestId);

      console.log("FROM_API", editPaymentRequest);
      setPaymentRequest(editPaymentRequest); // bez mergeanja s prev!
    }

    getPaymentRequestForEdit(id);
  }, [id]);

  const handleUpdateStudents = (students: PaymentRequestStudent[]) => {
      setPaymentRequest(prev => {
          const prevData = prev.studentsData;
          const selectedData: PaymentRequestStudentData[] = [];

          students.forEach(st => {
              if (st.isSelected) {
                  const existing = prevData && prevData.find(d => d.student.id === st.id);
                  selectedData.push(existing || { student: st });
              }
          });

          return {
              ...prev,
              students,
              studentsData: selectedData
          };
      });
  };


  const atleastOneItemSelected = (): boolean => {
    return (
      (paymentRequest.students != null &&
        paymentRequest.students.length > 0 &&
        paymentRequest.students.filter((s) => s.isSelected).length > 0) ||
      (paymentRequest.schoolCarriers != null &&
        paymentRequest.schoolCarriers.length > 0 &&
        paymentRequest.schoolCarriers.filter((sc) => sc.isSelected).length >
        0) ||
      (paymentRequest.authorizedCarriers != null &&
        paymentRequest.authorizedCarriers.length > 0 &&
        paymentRequest.authorizedCarriers.filter((ac) => ac.isSelected).length >
        0)
    );
  };

  const handleUpdateStudentsData = (
    studentsData: PaymentRequestStudentData[]
  ) => {
    setPaymentRequest((prev) => ({ ...prev, studentsData: [...studentsData] }));
  };

  const handleUpdateSchoolCarriers = (schoolCarriers: PaymentRequestSchoolCarrier[]) => {
      setPaymentRequest(prev => {
          const prevData = prev.schoolCarriersData;
          const selectedData: PaymentRequestSchoolCarrierData[] = [];

          schoolCarriers.forEach(sc => {
              if (sc.isSelected) {
                  const existing = prevData && prevData.find(d => d.schoolCarrier.id === sc.id);
                  selectedData.push(existing || { schoolCarrier: sc });
              }
          });

          return {
              ...prev,
              schoolCarriers,
              schoolCarriersData: selectedData
          };
      });
  };


  const handleUpdateSchoolCarriersData = (
    schoolCarriersData: PaymentRequestSchoolCarrierData[]
  ) => {
    setPaymentRequest((prev) => ({
      ...prev,
      schoolCarriersData: [...schoolCarriersData],
    }));
  };

  const handleUpdateAuthorizedCarriers = (authorizedCarriers: PaymentRequestAuthorizedCarrier[]) => {
      setPaymentRequest(prev => {
          const prevData = prev.authorizedCarriersData;
          const selectedData: PaymentRequestAuthorizedCarrierData[] = [];

          authorizedCarriers.forEach(ac => {
              if (ac.isSelected) {
                  const existing = prevData && prevData.find(d => d.authorizedCarrier.id === ac.id);
                  selectedData.push(existing || { authorizedCarrier: ac });
              }
          });

          return {
              ...prev,
              authorizedCarriers,
              authorizedCarriersData: selectedData
          };
      });
  };


  const handleUpdateAuthorizedCarriersData = (
    authorizedCarriersData: PaymentRequestAuthorizedCarrierData[]
  ) => {
    setPaymentRequest((prev) => ({
      ...prev,
      authorizedCarriersData: [...authorizedCarriersData],
    }));
  };

  const handleUpdateSchoolType = (schoolTypeId: number) => {
    setPaymentRequest(prev => {
      const isNew = !prev.id;  // koristi isti snapshot
      return {
        ...prev,
        schoolTypeId,
        studentsData: isNew ? [] : prev.studentsData,
        schoolCarriersData: isNew ? [] : prev.schoolCarriersData,
        authorizedCarriersData: isNew ? [] : prev.authorizedCarriersData,
      };
    });
  };


  const handleUpdateSchoolTypes = (schoolTypes: SchoolType[]) => {
    setSchoolTypes(schoolTypes);
  };



  const handleUpdateSchoolYear = (schoolYearId: number) => {
    async function getPrice() {
      const apiClient = new PaymentRequestApiClient();
      const pricingRequest = await apiClient.getAnualPricing();
      const pricing = pricingRequest.filter(price => price.schoolYearId === schoolYearId)[0];

      setPaymentRequest(prev => {
        const isNew = !prev.id; // koristimo isti snapshot

        return {
          ...prev,
          schoolYearId: schoolYearId,
          annualAdministration: pricing,
          // resetiraj samo kod novog zahtjeva
          studentsData: isNew ? [] : prev.studentsData,
          schoolCarriersData: isNew ? [] : prev.schoolCarriersData,
          authorizedCarriersData: isNew ? [] : prev.authorizedCarriersData,
        };
      });
    }

    getPrice();
  };

  const handleUpdateSchoolYears = (schoolYears: SchoolYear[]) => {
    setSchoolYears(schoolYears);
  };

  const handleUpdateMonth = (monthDate?: Date) => {
    setPaymentRequest(prev => {
      const isNew = !prev.id; // opet isti snapshot

      return {
        ...prev,
        monthDate: monthDate,
        // resetiraj samo kod novog zahtjeva
        studentsData: isNew ? [] : prev.studentsData,
        schoolCarriersData: isNew ? [] : prev.schoolCarriersData,
        authorizedCarriersData: isNew ? [] : prev.authorizedCarriersData,
      };
    });
  };

  const handleFinish = () => {
    async function createPaymentRequest() {
      const apiClient = new PaymentRequestApiClient();
      await apiClient.createPaymentRequest(paymentRequest);

      setShowWarningMessage(false);
      history.push(paymentRequestsUrl());
    }

    async function finishPaymentRequest() {
      const apiClient = new PaymentRequestApiClient();
      await apiClient.finishPaymentRequest(paymentRequest);

      setShowWarningMessage(false);
      history.push(paymentRequestsUrl());
    }

    if (id || (paymentRequest.id && paymentRequest.id > 0)) {
      finishPaymentRequest();
    } else {
      createPaymentRequest();
    }
  };

  const handleSaveDraft = async () => {
    async function createPaymentRequestDraft() {
      if (!atleastOneItemSelected()) {
        openSnackbar("Potrebno je odabrati barem jednu stavku.", "error");
      } else {
        const apiClient = new PaymentRequestApiClient();
        const paymentRequestId = await apiClient
          .createPaymentRequestDraft({
            ...paymentRequest,
            studentsData: [...paymentRequest.studentsData],
          })
          .then((response) => {
            openSnackbar("Zahtjev je privremeno pohranjen.", "success");
            return response;
          });

        const editPaymentRequest = await apiClient.getPaymentRequestForEdit(paymentRequestId);
        setPaymentRequest((prev) => ({ ...prev, ...editPaymentRequest }));
      }
    }

    async function updatePaymentRequestDraft() {
      if (!atleastOneItemSelected()) {
        openSnackbar("Potrebno je odabrati barem jednu stavku.", "error");
      } else {
        const apiClient = new PaymentRequestApiClient();
        await apiClient
          .updatePaymentRequestDraft({
            ...paymentRequest,
            studentsData: [...paymentRequest.studentsData],
          })
          .then((response) => {
            openSnackbar("Zahtjev je privremeno pohranjen.", "success");
            return response;
          });

        if (paymentRequest.id != null) {
          const updatedPaymentRequest = await apiClient.getPaymentRequestForEdit(paymentRequest.id);
          setPaymentRequest((prev) => ({ ...prev, ...updatedPaymentRequest }));
        }
      }
    }

    if (paymentRequest.id) {
      await updatePaymentRequestDraft();
    } else {
      await createPaymentRequestDraft();
    }
  };





  const getTitles = (): string[] => {
    var titles = [
      "Odabir školskih prijevoza, ovlaštenih prijevoznika i učenika",
    ];

    if (
      paymentRequest.schoolCarriers.filter((sc) => sc.isSelected).length > 0
    ) {
      titles.push("Unos podataka o školskim prijevozima");
    }
    if (
      paymentRequest.authorizedCarriers.filter((ac) => ac.isSelected).length > 0
    ) {
      titles.push("Unos podataka o ovlaštenim prijevoznicima");
    }
    if (paymentRequest.students.filter((s) => s.isSelected).length > 0) {
      titles.push("Unos podataka o učenicima");
    }

    titles.push("Potvrda zahtjeva");

    return titles;
  };

  const deletePaymentRequest = (id: number) => async () => {
    const apiClient = new PaymentRequestApiClient();
    const response = await apiClient.deletePaymentRequest(id);

    setShowWarningMessage(false);
    history.push(paymentRequestsUrl());
  };

  const selectStep = (
    <PaymentRequestSelectStep
      key={1}
      studentsState={paymentRequest.students}
      schoolCarriersState={paymentRequest.schoolCarriers}
      authorizedCarriersState={paymentRequest.authorizedCarriers}
      schoolTypeId={paymentRequest.schoolTypeId}
      schoolTypes={schoolTypes}
      schoolYearId={paymentRequest.schoolYearId}
      currency={paymentRequest.currency}
      schoolYears={schoolYears}
      monthDate={paymentRequest.monthDate}
      disableMonthPick={id != null || paymentRequest.id != null}
      updateStudents={handleUpdateStudents}
      updateSchoolCarriers={handleUpdateSchoolCarriers}
      updateAuthorizedCarriers={handleUpdateAuthorizedCarriers}
      updateSchoolType={handleUpdateSchoolType}
      updateSchoolTypes={handleUpdateSchoolTypes}
      updateSchoolYear={handleUpdateSchoolYear}
      updateSchoolYears={handleUpdateSchoolYears}
      updateMonth={handleUpdateMonth}
    />
  );

  const schoolCarrierDataStep = (
    <PaymentRequestSchoolCarrierDataStep
      key={2}
      schoolCarriers={paymentRequest.schoolCarriers}
      schoolCarriersDataState={paymentRequest.schoolCarriersData}
      schoolYearId={paymentRequest.schoolYearId}
      currency={paymentRequest.currency}
      updateSchoolCarriersData={handleUpdateSchoolCarriersData}
    />
  );

  const authorizedCarrierDataStep = (
    <PaymentRequestAuthorizedCarrierDataStep
      key={2}
      authorizedCarriers={paymentRequest.authorizedCarriers}
      authorizedCarriersDataState={paymentRequest.authorizedCarriersData}
      schoolYearId={paymentRequest.schoolYearId}
      currency={paymentRequest.currency}
      updateAuthorizedCarriersData={handleUpdateAuthorizedCarriersData}
    />
  );

  const studentDataStep = (
    <PaymentRequestStudentDataStep
      key={3}
      students={paymentRequest.students}
      studentsDataState={paymentRequest.studentsData}
      schoolYearId={paymentRequest.schoolYearId}
      annualAdministration={paymentRequest.annualAdministration}
      month={paymentRequest.monthDate}
      currency={paymentRequest.currency}
      updateStudentsData={handleUpdateStudentsData}
      setLatestStudentsData={setLatestStudentsDataFromStep}
    />
  );


  const dataConfirmationStep = (
    <PaymentRequestDataConfirmationStep
      key={4}
      paymentRequest={paymentRequest}
    />
  );

  const getSteps = () => {
    let steps: JSX.Element[] = [];
    let isSchoolCarrierSelected =
      paymentRequest.schoolCarriers.filter((sc) => sc.isSelected).length > 0;
    let isAuthorizedCarrierSelected =
      paymentRequest.authorizedCarriers.filter((ac) => ac.isSelected).length >
      0;
    let isStudentSelected =
      paymentRequest.students.filter((s) => s.isSelected).length > 0;

    steps.push(selectStep);

    if (isSchoolCarrierSelected) {
      steps.push(schoolCarrierDataStep);
    }
    if (isAuthorizedCarrierSelected) {
      steps.push(authorizedCarrierDataStep);
    }
    if (isStudentSelected) {
      steps.push(studentDataStep);
    }

    steps.push(dataConfirmationStep);

    return steps;
  };

  const hasUnsavedChanges = showWarningMessage && paymentRequest.monthDate !== null;


  return (
    <>
      <StepperComponent
        steps={getTitles()}
        previousButtonText={"Prethodni korak"}
        nextButtonText={"Sljedeći korak"}
        lastStepButtonText={"Predaj zahtjev"}
        confirmationModalTitle={"Predaja zahtjeva"}
        confirmationModalMessage={
          "Potvrdom ove akcije šaljete mjesečni zahtjev na daljnju obradu Ministarstvu znanosti i obrazovanja. Jeste li sigurni da želite predati zatjev?"
        }
        confirmationModalCancelBtnText="Odustani"
        confirmationModalOkBtnText="Predaj zahtjev"
        onFinish={handleFinish}
        saveBtn
        onSave={handleSaveDraft}
        deleteBtn={paymentRequest.id != null && paymentRequest.id > 0}
        onDelete={
          paymentRequest.id != null && paymentRequest.id > 0
            ? deletePaymentRequest(paymentRequest.id)
            : undefined
        }
        isFinancingRequest={false}
      >
        {getSteps()}
      </StepperComponent>
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
      <Prompt
        when={hasUnsavedChanges}
        message="Potvrdom ove akcije izgubit ćete podatke koje niste pohranili na trenutnom zahtjevu. Jeste li sigurni da želite odustati od ispunjavanja zahtjeva?"
      />

    </>
  );
}
